import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getMyNotifications, markNotificationsSeen, type InAppNotification } from '@/api/users';

/** Event types that deep-link to the status modal overlay on the home route. */
const STATUS_MODAL_EVENTS = new Set([
  'preview_link_added',
  'preview_feedback_added',
  'preview_approved',
  'preview_expiry_reminder_sent',
  'comment_added',
]);

/**
 * Notifications state + behavior for the suite shell.
 *
 * Extracted from the legacy CT `Sidebar` so the deep-link and unread-count
 * logic is shared verbatim between the old and new sidebars during soak.
 */
export function useSuiteNotifications({ enabled = true }: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['my-notifications'],
    queryFn: () => getMyNotifications(30),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const notifications = useMemo(() => data?.notifications ?? [], [data?.notifications]);
  const lastSeenAt = data?.last_seen_at ?? null;
  const totalCount = data?.total_count ?? 0;

  const unreadCount = lastSeenAt
    ? notifications.filter((n) => n.created_at > lastSeenAt).length
    : notifications.length;

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev && notifications.length > 0) {
        markNotificationsSeen()
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
          })
          .catch(() => {
            // Silently ignore — best-effort seen marking.
          });
      }
      return !prev;
    });
  }, [notifications, queryClient]);

  const close = useCallback(() => setOpen(false), []);

  const clickNotification = useCallback((notification: InAppNotification) => {
    setOpen(false);

    if (notification.link_path && notification.link_param && notification.link_value) {
      window.location.href = `${notification.link_path}?${notification.link_param}=${encodeURIComponent(notification.link_value)}`;
      return;
    }
    if (notification.link_path) {
      window.location.href = notification.link_path;
      return;
    }
    if (notification.campaign_id && STATUS_MODAL_EVENTS.has(notification.event_type)) {
      window.location.href = `/?campaign=${encodeURIComponent(notification.campaign_id)}&action=status`;
      return;
    }
    if (notification.campaign_id) {
      window.location.href = `/?campaign=${encodeURIComponent(notification.campaign_id)}`;
    }
  }, []);

  return {
    open,
    notifications,
    lastSeenAt,
    totalCount,
    unreadCount,
    toggle,
    close,
    clickNotification,
  };
}
